import { Group, Paper, Stack, useMatches } from "@mantine/core";

import type { ChatTask } from "@/sdk/agent/chat";

import { formatPartnerName } from "@/shared-types";
import { useStopwatch } from "@/shared-utils";

import { CustomerDetails, PartnerBadge, ProblemStatement } from "../CustomerSections";
import { ElapsedTimeValue } from "../CustomerSections/ElapsedTimeValue";
import { getOfferInfoBadgeType } from "../CustomerSections/OfferInfoBadge/getInfoBadgeType";
import { OfferInfoBadge } from "../CustomerSections/OfferInfoBadge/OfferInfoBadge";
import CloseChatButton from "./CloseChatButton";
import EndChatButton from "./EndChatButton";

function ChatElapsedTime({ startTime }: { startTime: number }) {
    const formattedChatTime = useStopwatch(startTime, true);
    const [timeValue, timeUnit] = formattedChatTime.split(" ");

    return <ElapsedTimeValue value={timeValue} unit={timeUnit} />;
}

export default function ChatControlsBar({ chatTask }: { chatTask: ChatTask }) {
    const { assignedAt, mdn, partner, customerName, problemStatement, topic } = chatTask;
    const formattedPartnerName = formatPartnerName(partner);
    const buttonGroupGap = useMatches({
        base: "xs",
        lg: "md",
    });
    const buttonSize = useMatches({
        base: "xs",
        lg: "sm",
    });
    // !  assignedAt can become undefined when the task enters wrapping state
    const startTime = assignedAt?.getTime() ?? null;
    const isWrapping = chatTask.status === "wrapping";
    const offerInfoBadgeType = getOfferInfoBadgeType(partner, topic);

    return (
        <Paper p="sm" radius={0} bg="var(--exwo-color-control-bar-bg)" h="100%" w="100%">
            <Group justify="space-between" align="stretch" wrap="nowrap" flex={1} h="100%" w="100%">
                <Stack gap="3xs" flex={1} h="100%" justify="center">
                    <CustomerDetails customerName={customerName} mdn={mdn} hideDivider={true} showCopyMdnIcon={true} />
                    <Group gap="xs" wrap="nowrap" h="100%" align="center">
                        {offerInfoBadgeType ? (
                            <OfferInfoBadge type={offerInfoBadgeType} />
                        ) : (
                            <PartnerBadge partner={formattedPartnerName} color="dark.4" c="gray.4" />
                        )}
                        <ProblemStatement problemStatement={problemStatement} />
                    </Group>
                </Stack>
                <Group gap={buttonGroupGap} align="center" justify="right" wrap="wrap-reverse" h="100%">
                    {startTime && !isWrapping && <ChatElapsedTime startTime={startTime} />}
                    {chatTask.status === "assigned" && <EndChatButton chatTask={chatTask} size={buttonSize} />}
                    {chatTask.status === "wrapping" && <CloseChatButton chatTask={chatTask} size={buttonSize} />}
                </Group>
            </Group>
        </Paper>
    );
}
